import { useMemo, useState, useEffect, useRef } from "react";
import { useGeolocation } from "./hooks/useGeolocation";
import { useLiveVehicles } from "./hooks/useBusData";
import { useLiveTaxis } from "./hooks/useLiveTaxis";
import { usePlymouthStops } from "./hooks/usePlymouthStops";
import { getNearbyStops } from "./utils/bods";
import BusMap from "./components/BusMap";
import LocalAreaBoard from "./components/LocalAreaBoard";
import VehicleDetailModal from "./components/VehicleDetailModal";
import LocationDeniedPopup from "./components/LocationDeniedPopup";
import "./App.css";

const STOP_RADIUS_KM = 0.2; // 200m — show only stops within 200m around the selected pin (as requested)
const STOP_LIST_LIMIT = 30;
const PLYMOUTH_CENTER = { lat: 50.3714, lng: -4.1425 }; // Royal Parade, Plymouth city centre
const COVERAGE_RADIUS_KM = 10; // distanța max de Plymouth în care arătăm stop-uri (în afara → mesaj "outside coverage")

// Distanță Haversine în km
function haversineKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

export default function App() {
  // map+list screen: header, map (stops + live buses), location button, stop list.
  const { location, loading: locLoading, error: locError, request: requestLocation, isFallback, denied } = useGeolocation();
  const [showPopup, setShowPopup] = useState(false);

  // Also show popup when user denies the native prompt.
  useEffect(() => {
    if (denied) setShowPopup(true);
  }, [denied]);
  const { vehicles, error: vehicleError, lastUpdated } = useLiveVehicles();
  const { taxis, error: taxiError, lastUpdated: taxiUpdated } = useLiveTaxis();
  const { stops: plymouthStops, loading: stopsLoading } = usePlymouthStops();
  const [search, setSearch] = useState("");
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedStop, setSelectedStop] = useState(null);
  // The visible map centre (lat/lng). The stops drawn/list follow THIS, not the
  // fixed GPS `location`, so when the user pans/zooms the map (e.g. towards the
  // city centre) the stops re-centre on the screen like Google Maps.
  const [mapView, setMapView] = useState(null);

  // Custom feed (POST /api/departures) — merged into live vehicles.
  const [customFeed, setCustomFeed] = useState({ vehicles: [], updatedAt: null });
  useEffect(() => {
    let cancelled = false;
    let interval;
    const load = async () => {
      try { const r = await fetch("/api/departures"); if (!r.ok) return; const d = await r.json(); if (!cancelled) setCustomFeed(d); }
      catch (_) {}
    };
    load();
    interval = setInterval(load, 15000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  const allVehicles = useMemo(() => {
    const merged = [...vehicles];
    for (const v of customFeed.vehicles || []) {
      if (v.vehicleRef && merged.some((m) => m.vehicleRef === v.vehicleRef)) continue;
      merged.push(v);
    }
    return merged;
  }, [vehicles, customFeed]);

  // Stop list: nearest first once location is granted; else alphabetical/all.
  // Când locația e departe de Plymouth (> COVERAGE_RADIUS_KM), NU afișăm stop-uri false din Plymouth.
  const outsidePlymouth = Boolean(location && haversineKm(location.lat, location.lng, PLYMOUTH_CENTER.lat, PLYMOUTH_CENTER.lng) > COVERAGE_RADIUS_KM);

  // Initialise the "visible map centre" to the user's GPS fix whenever a
  // location arrives (or changes). From then on the centre follows the map
  // (pan/zoom via onMapViewChange), so stops track the screen, not the GPS.
  useEffect(() => {
    if (location?.lat !== undefined && location?.lng !== undefined) {
      setMapView((prev) => ({ lat: location.lat, lng: location.lng, zoom: prev?.zoom ?? 16 }));
    }
  }, [location?.lat, location?.lng]);

  // The anchor point used for "nearest to centre": the visible map centre
  // when we have a map + location, otherwise the GPS fix / Plymouth centre.
  const viewCenter = mapView && mapView.lat !== undefined
    ? mapView
    : (location && location.lat !== undefined ? location : null);

  // Radius of stops to show, in km, around the visible centre. Grows as the
  // user zooms out so the map keeps showing a useful ring of stops, and
  // shrinks when zoomed in close (Google-Maps feel). ~ 200m at z18 … ~4km z11.
  const viewZoom = mapView?.zoom ?? (location ? 16 : 12);
  const centerRadiusKm = Math.round((900 / (2 ** (viewZoom - 14))) * 100) / 100; // z14≈~0.9km

  // Stops nearest the visible map centre (screen-following), used for map +
  // the "nearest to centre" list. Kept empty when outside coverage.
  const nearbyStops = useMemo(() => {
    if (!location) return [];
    if (outsidePlymouth || !viewCenter) return [];
    return getNearbyStops(viewCenter.lat, viewCenter.lng, centerRadiusKm, plymouthStops);
  }, [location, outsidePlymouth, viewCenter, centerRadiusKm, plymouthStops]);

  // Auto-select the closest stop once the user's location arrives so the
  // Departure Board opens for the nearest stop (Enable Location → select the
  // closest stop → show its board). Fires once per GPS fix, and NOT when the
  // user pans/zooms the map (that only moves mapView, not location).
  const autoSelectedKeyRef = useRef(null);
  useEffect(() => {
    if (!location || outsidePlymouth) return;
    if (!nearbyStops.length) return;
    const key = location.lat.toFixed(4) + "," + location.lng.toFixed(4);
    if (autoSelectedKeyRef.current === key) return; // already done for this fix
    autoSelectedKeyRef.current = key;
    setSelectedStop(nearbyStops[0]); // nearest stop → its Departure Board opens
  }, [location, outsidePlymouth, nearbyStops]);

  const shownStops = useMemo(() => {
    // Când ești în afara Plymouth: fără stop-uri false — lista rămâne goală, doar mesajul de acoperire.
    if (outsidePlymouth) return [];
    const q = search.trim().toLowerCase();
    const base = nearbyStops.length ? nearbyStops :
      ([...plymouthStops].sort((a, b) => a.name.localeCompare(b.name || "")));
    if (!q) return base.slice(0, STOP_LIST_LIMIT);
    return base.filter((s) => [s.name, s.locality, s.street, s.id].filter(Boolean).some((v) => v.toLowerCase().includes(q))).slice(0, STOP_LIST_LIMIT);
  }, [outsidePlymouth, nearbyStops, plymouthStops, search]);

  // Stops drawn on the map:
  // - cu selectedStop → DOAR cele din 400m în jurul lui (ca la zoom pe stație să se vadă doar acesea)
  // - fără selectedStop + location → nearby (400m de user)
  // - fără selectedStop și fără location → toate Plymouth (harta goală cu toate stațiile)
  const mapStops = useMemo(() => {
    if (selectedStop) return getNearbyStops(selectedStop.lat, selectedStop.lng, STOP_RADIUS_KM, plymouthStops);
    return location ? nearbyStops : plymouthStops;
  }, [selectedStop, location, nearbyStops, plymouthStops]);

  return (
    <div className="app app--map-list">
      <header className="header">
        <div className="header-icon" aria-hidden="true"><svg width="26" height="26" viewBox="0 0 24 24" fill="#fff" xmlns="http://www.w3.org/2000/svg"><rect x="4" y="3" width="16" height="13" rx="2" fill="#fff"/><path d="M4 12 h16 v2 h-16 z" fill="#d0f0e8"/><rect x="5.5" y="6" width="3" height="3" rx="0.6" fill="#0066cc"/><rect x="10" y="6" width="3" height="3" rx="0.6" fill="#0066cc"/><rect x="14.5" y="6" width="3" height="3" rx="0.6" fill="#0066cc"/><circle cx="7" cy="17.5" r="1.8" fill="#333"/><circle cx="17" cy="17.5" r="1.8" fill="#333"/><rect x="6" y="16.5" width="2" height="1.4" fill="#333"/><rect x="16" y="16.5" width="2" height="1.4" fill="#333"/></svg></div>
        <div>
          <h1>Getting Around — Live Data</h1>
        </div>
      </header>

      {selectedStop ? (
        /* Când selectezi un stop: harta face zoom și arată DOAR stop-urile din 400m în jurul pin-ului selectat */
        <>
          <BusMap
            stops={mapStops}
            vehicles={allVehicles}
            taxis={taxis}
            userLocation={null}
            selectedStop={selectedStop}
            onStopClick={setSelectedStop}
            onVehicleClick={setSelectedVehicle}
          />
          <LocalAreaBoard
            anchorStop={selectedStop}
            stops={mapStops}
            vehicles={allVehicles}
            onBack={() => setSelectedStop(null)}
            hideStationMap
          />
        </>
      ) : !location ? (
        /* Ecranul 1: harta Plymouth City Centre în spate + Enable location peste */
        <>
          <BusMap
            stops={plymouthStops}
            vehicles={[]}
            taxis={taxis}
            userLocation={null}
            selectedStop={selectedStop}
            onStopClick={setSelectedStop}
            onVehicleClick={setSelectedVehicle}
          />
          <div className="loc-welcome">
            <div className="loc-welcome-ic" aria-hidden="true"><svg width="52" height="52" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M12 2 C7 2 4 6 4 10.5 C4 16 12 22 12 22 C12 22 20 16 20 10.5 C20 6 17 2 12 2 Z" fill="#e53935"/><circle cx="12" cy="10.3" r="3.4" fill="#fff"/></svg></div>
            <h2>Enable Location</h2>
            <p>We need your location to show nearby bus stops and sort them by distance. Please tap Allow when prompted.</p>
            {!locLoading && (
              <button className="loc-btn loc-btn--big" onClick={() => { requestLocation(); }}>
                Enable location
              </button>
            )}
            {!locLoading && (
              <button className="loc-btn-skip" onClick={() => setShowPopup(false)}>Not now</button>
            )}
            {locLoading && <div className="info-box">Finding your location…</div>}
            {locError && locError !== "permission_denied" && !location && (
              <div className="info-box info-box--amber">{locError}</div>
            )}
          </div>
        </>
      ) : (
        <>
      <BusMap
        stops={mapStops}
        vehicles={allVehicles}
        taxis={taxis}
        userLocation={location}
        selectedStop={selectedStop}
        onStopClick={setSelectedStop}
        onVehicleClick={setSelectedVehicle}
        onMapViewChange={setMapView}
      />

      {isFallback && !locError && (
        <div className="loc-hint-inline">Showing all Plymouth stops — no location set.</div>
      )}

      {locLoading && <div className="info-box">Finding your location…</div>}
      {locError && locError !== "permission_denied" && !location && (
        <div className="info-box info-box--amber">{locError}</div>
      )}


      {/* Stop list */}
      {selectedStop ? (
        <LocalAreaBoard
          anchorStop={selectedStop}
          stops={mapStops}
          vehicles={allVehicles}
          onBack={() => setSelectedStop(null)}
        />
      ) : (
        <>
      <div className="section-head">
        <div className="section-label">
          {outsidePlymouth ? "Bus stops" : location ? "Nearby bus stops" : "All Plymouth bus stops"}
        </div>
        <div className="section-subtitle">
          {outsidePlymouth
            ? "Outside Plymouth coverage"
            : stopsLoading ? "Loading stops…" : `${shownStops.length} of ${location ? nearbyStops.length : plymouthStops.length} stops${location ? " · nearest first" : ""}`}
        </div>
      </div>
      <div className="card">
        {outsidePlymouth ? (
          <div className="empty-state">
            No bus data near your location — outside Plymouth coverage.
            <br /><small>The map follows your position; bus stop data is currently available only in the Plymouth area.</small>
          </div>
        ) : (
        <>
        {stopsLoading && <div className="empty-state">Loading stops…</div>}
        {!stopsLoading && shownStops.length === 0 && <div className="empty-state">No stops matched your search.</div>}
        {!stopsLoading && shownStops.map((stop) => (
          <button key={stop.id} className="stop-row" onClick={() => setSelectedStop(stop)}>
            <div className="stop-icon">🚏</div>
            <div className="stop-details">
              <div className="stop-name">{stop.name}</div>
              <div className="stop-meta">
                {stop.distanceKm !== undefined
                  ? `${stop.distanceKm < 1 ? `${Math.round(stop.distanceKm * 1000)}m` : `${stop.distanceKm.toFixed(1)}km`} away · `
                  : ""}
                {[stop.street, stop.locality, stop.id].filter(Boolean).join(" · ")}
              </div>
            </div>
            <span className="chevron">›</span>
          </button>
        ))}
        </>
        )}
      </div>
        </>
      )}
        </>
      )}

      {selectedVehicle && (
        <VehicleDetailModal vehicle={selectedVehicle} userLocation={location} onClose={() => setSelectedVehicle(null)} />
      )}
    </div>
  );
}
